import { useState, useMemo } from "react";
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import RefundDialog from "../RefundDialog";
import RegistrationDetailPanel from "./RegistrationDetailPanel";
import {
  Users,
  CheckCircle,
  Clock,
  X,
  Eye,
  Mail,
  Check,
  MoreVertical,
  RefreshCw,
  Ban,
  ClipboardCheck,
  Download,
  User,
  Calendar,
  UserCircle,
  Phone,
  Heart,
  CheckSquare,
  Square,
  XCircle,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  UserPlus,
  DollarSign,
  Loader2,
  Search,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

// Helper to calculate age from date of birth
const calculateAge = (dob) => {
  if (!dob) return null;
  const birthDate = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
};

// Helper to format date for export
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  try {
    return new Date(dateStr).toLocaleDateString();
  } catch {
    return dateStr;
  }
};

// Format DOB nicely
const formatDOB = (dob) => {
  if (!dob) return '-';
  try {
    return new Date(dob).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return dob;
  }
};

// ==================== SORTABLE HEADER ====================
const SortHeader = ({ label, field, sortField, sortDir, onSort, className = "" }) => {
  const active = sortField === field;
  return (
    <th
      className={`pb-3 font-medium cursor-pointer select-none hover:text-slate-900 transition-colors ${className}`}
      onClick={() => onSort(field)}
    >
      <div className="flex items-center gap-1">
        {label}
        {active ? (
          sortDir === "asc" ? <ArrowUp className="w-3.5 h-3.5" /> : <ArrowDown className="w-3.5 h-3.5" />
        ) : (
          <ArrowUpDown className="w-3.5 h-3.5 opacity-30" />
        )}
      </div>
    </th>
  );
};

// ==================== MAIN COMPONENT ====================
export const ProgramRegistrationsTab = ({
  program,
  programId,
  registrations,
  teams,
  token,
  API,
  onRefresh,
}) => {
  const [registrationSubTab, setRegistrationSubTab] = useState("all");
  const [detailPanelOpen, setDetailPanelOpen] = useState(false);
  const [detailRegistration, setDetailRegistration] = useState(null);
  const [refundDialogOpen, setRefundDialogOpen] = useState(false);
  const [refundRegistration, setRefundRegistration] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  // Sorting
  const [sortField, setSortField] = useState("name");
  const [sortDir, setSortDir] = useState("asc");

  // Multi-select
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Bulk action dialogs
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);

  const [teamDialogOpen, setTeamDialogOpen] = useState(false);
  const [selectedTeamId, setSelectedTeamId] = useState("");
  const [assigningTeam, setAssigningTeam] = useState(false);

  const [bulkRefundDialogOpen, setBulkRefundDialogOpen] = useState(false);
  const [bulkRefunding, setBulkRefunding] = useState(false);

  const [bulkStatusDialogOpen, setBulkStatusDialogOpen] = useState(false);
  const [bulkStatus, setBulkStatus] = useState("");
  const [bulkUpdatingStatus, setBulkUpdatingStatus] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const handleOpenDetail = (registration) => {
    setDetailRegistration(registration);
    setDetailPanelOpen(true);
  };

  const handleOpenRefund = (registration) => {
    setRefundRegistration(registration);
    setRefundDialogOpen(true);
  };

  const handleRefundComplete = () => {
    onRefresh();
  };

  // Sorting handler
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  // Categorize registrations
  const confirmedRegistrations = registrations.filter(r => r.status === "confirmed");
  const pendingRegistrations = registrations.filter(r => r.status === "pending");
  const tryoutRegistrations = registrations.filter(r => r.status === "tryout");
  const waitlistedRegistrations = registrations.filter(r => r.status === "waitlisted");
  const declinedRegistrations = registrations.filter(r => r.status === "declined");
  const preRegisteredRegistrations = registrations.filter(r => r.status === "pre_registered");
  const activeRegistrations = [...confirmedRegistrations, ...pendingRegistrations];

  const isCompetitive = program?.program_type === "competitive";
  const isCamp = program?.program_type === "camp";

  // Build sub-tab buttons
  const renderSubTabs = () => {
    if (isCompetitive) {
      return (
        <>
          <Button variant={registrationSubTab === "all" ? "default" : "ghost"} size="sm" onClick={() => setRegistrationSubTab("all")} className={`rounded-full ${registrationSubTab === "all" ? "bg-slate-900 text-white" : ""}`}>
            All ({registrations.length})
          </Button>
          <Button variant={registrationSubTab === "tryout" ? "default" : "ghost"} size="sm" onClick={() => setRegistrationSubTab("tryout")} className={`rounded-full ${registrationSubTab === "tryout" ? "bg-blue-600 text-white" : ""}`}>
            <ClipboardCheck className="w-3.5 h-3.5 mr-1.5" />Tryouts ({tryoutRegistrations.length})
          </Button>
          <Button variant={registrationSubTab === "confirmed" ? "default" : "ghost"} size="sm" onClick={() => setRegistrationSubTab("confirmed")} className={`rounded-full ${registrationSubTab === "confirmed" ? "bg-green-600 text-white" : ""}`}>
            <CheckCircle className="w-3.5 h-3.5 mr-1.5" />Confirmed ({confirmedRegistrations.length})
          </Button>
          <Button variant={registrationSubTab === "pending" ? "default" : "ghost"} size="sm" onClick={() => setRegistrationSubTab("pending")} className={`rounded-full ${registrationSubTab === "pending" ? "bg-yellow-600 text-white" : ""}`}>
            <Clock className="w-3.5 h-3.5 mr-1.5" />Pending ({pendingRegistrations.length})
          </Button>
          <Button variant={registrationSubTab === "declined" ? "default" : "ghost"} size="sm" onClick={() => setRegistrationSubTab("declined")} className={`rounded-full ${registrationSubTab === "declined" ? "bg-red-600 text-white" : ""}`}>
            <X className="w-3.5 h-3.5 mr-1.5" />Declined ({declinedRegistrations.length})
          </Button>
        </>
      );
    }

    if (isCamp) {
      return (
        <>
          <Button variant={registrationSubTab === "all" ? "default" : "ghost"} size="sm" onClick={() => setRegistrationSubTab("all")} className={`rounded-full ${registrationSubTab === "all" ? "bg-slate-900 text-white" : ""}`}>
            All ({registrations.length})
          </Button>
          {program?.pre_registration_enabled && (
            <Button variant={registrationSubTab === "pre" ? "default" : "ghost"} size="sm" onClick={() => setRegistrationSubTab("pre")} className={`rounded-full ${registrationSubTab === "pre" ? "bg-violet-600 text-white" : ""}`}>
              <Clock className="w-3.5 h-3.5 mr-1.5" />Pre-Registered ({preRegisteredRegistrations.length})
            </Button>
          )}
          <Button variant={registrationSubTab === "confirmed" ? "default" : "ghost"} size="sm" onClick={() => setRegistrationSubTab("confirmed")} className={`rounded-full ${registrationSubTab === "confirmed" ? "bg-green-600 text-white" : ""}`}>
            <CheckCircle className="w-3.5 h-3.5 mr-1.5" />Confirmed ({activeRegistrations.length})
          </Button>
          <Button variant={registrationSubTab === "waitlist" ? "default" : "ghost"} size="sm" onClick={() => setRegistrationSubTab("waitlist")} className={`rounded-full ${registrationSubTab === "waitlist" ? "bg-yellow-600 text-white" : ""}`}>
            Waitlist ({waitlistedRegistrations.length})
          </Button>
        </>
      );
    }

    return (
      <>
        <Button variant={registrationSubTab === "all" ? "default" : "ghost"} size="sm" onClick={() => setRegistrationSubTab("all")} className={`rounded-full ${registrationSubTab === "all" ? "bg-slate-900 text-white" : ""}`}>
          All ({registrations.length})
        </Button>
        <Button variant={registrationSubTab === "confirmed" ? "default" : "ghost"} size="sm" onClick={() => setRegistrationSubTab("confirmed")} className={`rounded-full ${registrationSubTab === "confirmed" ? "bg-green-600 text-white" : ""}`}>
          <CheckCircle className="w-3.5 h-3.5 mr-1.5" />Confirmed ({confirmedRegistrations.length})
        </Button>
        <Button variant={registrationSubTab === "pending" ? "default" : "ghost"} size="sm" onClick={() => setRegistrationSubTab("pending")} className={`rounded-full ${registrationSubTab === "pending" ? "bg-yellow-600 text-white" : ""}`}>
          <Clock className="w-3.5 h-3.5 mr-1.5" />Pending ({pendingRegistrations.length})
        </Button>
        <Button variant={registrationSubTab === "waitlist" ? "default" : "ghost"} size="sm" onClick={() => setRegistrationSubTab("waitlist")} className={`rounded-full ${registrationSubTab === "waitlist" ? "bg-slate-600 text-white" : ""}`}>
          Waitlist ({waitlistedRegistrations.length})
        </Button>
      </>
    );
  };

  // Get display registrations based on sub-tab
  const getBaseRegs = () => {
    switch (registrationSubTab) {
      case "all": return registrations;
      case "tryout": return tryoutRegistrations;
      case "confirmed": return isCompetitive ? confirmedRegistrations : activeRegistrations;
      case "pending": return pendingRegistrations;
      case "declined": return declinedRegistrations;
      case "pre": return preRegisteredRegistrations;
      case "waitlist": return waitlistedRegistrations;
      default: return registrations;
    }
  };

  // Search filter
  const searchFiltered = useMemo(() => {
    const base = getBaseRegs();
    if (!searchQuery.trim()) return base;
    const q = searchQuery.toLowerCase();
    return base.filter(reg => {
      const name = `${reg.player?.first_name || ''} ${reg.player?.last_name || ''}`.toLowerCase();
      const email = (reg.payer?.email || reg.household?.primary_contact?.email || '').toLowerCase();
      const parentName = (reg.payer?.name || reg.household?.primary_contact?.name || '').toLowerCase();
      return name.includes(q) || email.includes(q) || parentName.includes(q);
    });
  }, [registrations, registrationSubTab, searchQuery]);

  // Sort
  const displayRegs = useMemo(() => {
    const sorted = [...searchFiltered];
    sorted.sort((a, b) => {
      let valA, valB;
      switch (sortField) {
        case "name":
          valA = `${a.player?.first_name || ''} ${a.player?.last_name || ''}`.toLowerCase();
          valB = `${b.player?.first_name || ''} ${b.player?.last_name || ''}`.toLowerCase();
          break;
        case "age":
          valA = calculateAge(a.player?.date_of_birth) ?? 999;
          valB = calculateAge(b.player?.date_of_birth) ?? 999;
          break;
        case "dob":
          valA = a.player?.date_of_birth || '';
          valB = b.player?.date_of_birth || '';
          break;
        case "gender":
          valA = (a.player?.gender || '').toLowerCase();
          valB = (b.player?.gender || '').toLowerCase();
          break;
        case "team":
          valA = (teams.find(t => t.team_id === a.team_id)?.name || '').toLowerCase();
          valB = (teams.find(t => t.team_id === b.team_id)?.name || '').toLowerCase();
          break;
        case "status":
          valA = a.status || '';
          valB = b.status || '';
          break;
        case "payment":
          valA = a.payment_status || '';
          valB = b.payment_status || '';
          break;
        case "total":
          valA = a.amount || 0;
          valB = b.amount || 0;
          break;
        case "paid":
          valA = a.amount_paid ?? 0;
          valB = b.amount_paid ?? 0;
          break;
        case "remaining":
          valA = a.amount_remaining ?? 0;
          valB = b.amount_remaining ?? 0;
          break;
        case "date":
          valA = a.created_at || '';
          valB = b.created_at || '';
          break;
        default:
          return 0;
      }
      if (valA < valB) return sortDir === "asc" ? -1 : 1;
      if (valA > valB) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [searchFiltered, sortField, sortDir, teams]);

  // Selection helpers
  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const allIds = displayRegs.map(r => r.registration_id);
    if (allIds.every(id => selectedIds.has(id))) {
      setSelectedIds(prev => {
        const next = new Set(prev);
        allIds.forEach(id => next.delete(id));
        return next;
      });
    } else {
      setSelectedIds(prev => {
        const next = new Set(prev);
        allIds.forEach(id => next.add(id));
        return next;
      });
    }
  };

  const selectedRegs = displayRegs.filter(r => selectedIds.has(r.registration_id));
  const selectedCount = selectedRegs.length;
  const allSelected = displayRegs.length > 0 && displayRegs.every(r => selectedIds.has(r.registration_id));

  // ==================== BULK ACTIONS ====================

  const handleBulkEmail = async () => {
    if (!emailSubject.trim() || !emailBody.trim()) {
      toast.error("Subject and message are required");
      return;
    }
    setSendingEmail(true);
    try {
      // Collect unique parent emails
      const emails = [...new Set(selectedRegs.map(r =>
        r.payer?.email || r.household?.primary_contact?.email || r.household_email
      ).filter(Boolean))];

      if (emails.length === 0) {
        toast.error("No email addresses found for selected players");
        setSendingEmail(false);
        return;
      }

      await axios.post(`${API}/programs/${programId}/bulk-email`, {
        registration_ids: selectedRegs.map(r => r.registration_id),
        subject: emailSubject,
        body: emailBody,
      }, { headers });

      toast.success(`Email sent to ${emails.length} recipient(s)`);
      setEmailDialogOpen(false);
      setEmailSubject("");
      setEmailBody("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send emails");
    } finally {
      setSendingEmail(false);
    }
  };

  const handleBulkAssignTeam = async () => {
    if (!selectedTeamId) {
      toast.error("Please select a team");
      return;
    }
    setAssigningTeam(true);
    try {
      await axios.post(`${API}/teams/${selectedTeamId}/players`, {
        registration_ids: selectedRegs.map(r => r.registration_id),
      }, { headers });

      const team = teams.find(t => t.team_id === selectedTeamId);
      toast.success(`${selectedCount} player(s) assigned to ${team?.name || 'team'}`);
      setTeamDialogOpen(false);
      setSelectedTeamId("");
      setSelectedIds(new Set());
      onRefresh();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : detail?.message || "Failed to assign team");
    } finally {
      setAssigningTeam(false);
    }
  };

  const handleBulkRefund = async () => {
    setBulkRefunding(true);
    try {
      const res = await axios.post(`${API}/programs/${programId}/bulk-refund`, {
        registration_ids: selectedRegs.map(r => r.registration_id),
      }, { headers });
      toast.success(`Refunded ${res.data.refunded || selectedCount} registration(s)`);
      setBulkRefundDialogOpen(false);
      setSelectedIds(new Set());
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to process refunds");
    } finally {
      setBulkRefunding(false);
    }
  };

  const handleBulkStatusChange = async () => {
    if (!bulkStatus) {
      toast.error("Please select a status");
      return;
    }
    setBulkUpdatingStatus(true);
    try {
      await axios.post(`${API}/programs/${programId}/bulk-update-status`, {
        registration_ids: selectedRegs.map(r => r.registration_id),
        status: bulkStatus,
      }, { headers });
      toast.success(`${selectedCount} registration(s) updated to ${bulkStatus}`);
      setBulkStatusDialogOpen(false);
      setBulkStatus("");
      setSelectedIds(new Set());
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update status");
    } finally {
      setBulkUpdatingStatus(false);
    }
  };

  // Export registrations to CSV
  const exportRegistrations = () => {
    const regsToExport = selectedCount > 0 ? selectedRegs : displayRegs;
    if (regsToExport.length === 0) {
      toast.error("No registrations to export");
      return;
    }

    const csvHeaders = [
      "First Name", "Last Name", "Age", "Gender", "Date of Birth",
      "Team/Group", "Status", "Payment Status", "Total", "Paid", "Remaining",
      "Payment Plan", "Registration Date", "Parent/Guardian Name", "Parent Email",
      "Parent Phone", "Address", "City", "State", "Zip",
      "Emergency Contact", "Emergency Phone", "Medical Conditions", "Allergies",
      "Jersey Size", "Short Size"
    ];

    const rows = regsToExport.map(reg => {
      const player = reg.player || {};
      const household = reg.household || {};
      const primaryContact = household.primary_contact || {};
      const address = household.address || player.address || {};
      const team = teams.find(t => t.team_id === reg.team_id);
      const age = calculateAge(player.date_of_birth);

      return [
        player.first_name || '', player.last_name || '',
        age !== null ? age : '', player.gender || '',
        formatDate(player.date_of_birth), team?.name || '',
        reg.status || '', reg.payment_status || '',
        reg.amount ? `$${reg.amount.toFixed(2)}` : '',
        reg.amount_paid != null ? `$${reg.amount_paid.toFixed(2)}` : '',
        reg.amount_remaining != null ? `$${reg.amount_remaining.toFixed(2)}` : '',
        reg.payment_plan || 'Full Payment', formatDate(reg.created_at),
        primaryContact.name || reg.payer?.name || '',
        primaryContact.email || reg.payer?.email || '',
        primaryContact.phone || '',
        address.street || address.line1 || '', address.city || '',
        address.state || '', address.zip || address.postal_code || '',
        player.emergency_contact_name || '', player.emergency_contact_phone || '',
        player.medical_conditions || '', player.allergies || '',
        reg.jersey_size || player.jersey_size || '',
        reg.short_size || player.short_size || ''
      ];
    });

    const csvContent = [
      csvHeaders.join(','),
      ...rows.map(row => row.map(cell => {
        const escaped = String(cell).replace(/"/g, '""');
        return escaped.includes(',') || escaped.includes('"') || escaped.includes('\n')
          ? `"${escaped}"` : escaped;
      }).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const programName = program?.name?.replace(/[^a-z0-9]/gi, '_') || 'program';
    const date = new Date().toISOString().split('T')[0];
    link.href = URL.createObjectURL(blob);
    link.download = `${programName}_registrations_${date}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
    toast.success(`Exported ${regsToExport.length} registrations`);
  };

  const getSubTabTitle = () => {
    switch (registrationSubTab) {
      case "all": return "All Registrations";
      case "tryout": return "Tryout Registrations";
      case "pre": return "Pre-Registered (Awaiting Payment)";
      case "confirmed": return "Confirmed Registrations";
      case "pending": return "Pending Registrations";
      case "declined": return "Declined Registrations";
      case "waitlist": return "Waitlist";
      default: return "Registrations";
    }
  };

  return (
    <div className="program-section program-registrations" data-program-section="registrations" data-testid="registrations-tab">
      <div className="program-section-heading"><div><h2>Registrations</h2><p>Review participants, manage registration status and follow up with families.</p></div></div>
      {/* Sub-tabs */}
      <div className="program-filter-bar">
        {renderSubTabs()}
      </div>

      {/* Pre-Registration Actions (camps) */}
      {isCamp && registrationSubTab === "pre" && preRegisteredRegistrations.length > 0 && (
        <div className="bg-violet-50 border border-violet-200 rounded-2xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-semibold text-violet-900">Pre-Registration Actions</h4>
              <p className="text-sm text-violet-700">
                {preRegisteredRegistrations.length} people are waiting to pay.
                {program?.payment_due_date && ` Payment due: ${new Date(program.payment_due_date).toLocaleDateString()}`}
              </p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="border-violet-300 text-violet-700 hover:bg-violet-100"
                onClick={async () => {
                  if (window.confirm("Send payment request emails to all pre-registered users?")) {
                    try {
                      await axios.post(`${API}/programs/${programId}/send-payment-requests`, { send_to_all: true }, { headers });
                      toast.success("Payment requests sent!");
                    } catch (err) { toast.error("Failed to send payment requests"); }
                  }
                }}>
                <Mail className="w-4 h-4 mr-1.5" />Send Payment Requests
              </Button>
              <Button size="sm" className="bg-violet-600 hover:bg-violet-700 text-white"
                onClick={async () => {
                  if (window.confirm("Convert all pre-registrations to pending?")) {
                    try {
                      const res = await axios.post(`${API}/programs/${programId}/convert-all-pre-registrations`, {}, { headers });
                      toast.success(`Converted ${res.data.converted_to_pending} registrations!`);
                      onRefresh();
                    } catch (err) { toast.error("Failed to convert pre-registrations"); }
                  }
                }}>
                <Check className="w-4 h-4 mr-1.5" />Open All for Payment
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Tryout Actions (competitive) */}
      {isCompetitive && registrationSubTab === "tryout" && tryoutRegistrations.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-semibold text-blue-900">Tryout Management</h4>
              <p className="text-sm text-blue-700">
                {tryoutRegistrations.length} players registered for tryouts.
                {program?.tryouts_date && ` Tryout date: ${new Date(program.tryouts_date).toLocaleDateString()}`}
              </p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="border-blue-300 text-blue-700 hover:bg-blue-100"
                onClick={() => toast.info("Tryout evaluation coming soon!")}>
                <ClipboardCheck className="w-4 h-4 mr-1.5" />Evaluate Players
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Registration Table */}
      <div className="program-data-panel">
        <div className="program-data-toolbar">
          <h3 className="font-semibold text-slate-900">{getSubTabTitle()}</h3>
          <div className="flex items-center gap-2 flex-wrap">
            {/* Search */}
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                aria-label="Search registrations"
                type="text"
                placeholder="Search players..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-full focus:outline-none focus:ring-2 focus:ring-slate-300 w-48"
              />
              {searchQuery && (
                <button aria-label="Clear registration search" onClick={() => setSearchQuery("")} className="absolute right-3 top-1/2 -translate-y-1/2">
                  <XCircle className="w-3.5 h-3.5 text-slate-400 hover:text-slate-600" />
                </button>
              )}
            </div>

            <Button variant="outline" size="sm" className="rounded-full" onClick={exportRegistrations} data-testid="export-registrations-btn">
              <Download className="w-4 h-4 mr-2" />{selectedCount > 0 ? `Export ${selectedCount}` : 'Export CSV'}
            </Button>
            {registrationSubTab === "pending" && pendingRegistrations.length > 0 && (
              <Button variant="outline" size="sm" className="rounded-full text-violet-600 border-violet-200 hover:bg-violet-50"
                onClick={async () => {
                  try {
                    const res = await axios.post(`${API}/programs/${programId}/send-completion-reminders`, {}, { headers });
                    toast.success(`Sent ${res.data.sent_count} reminder email(s)`);
                  } catch (err) { toast.error(err.response?.data?.detail || "Failed to send reminders"); }
                }}>
                <Mail className="w-4 h-4 mr-2" />Send Completion Reminders
              </Button>
            )}
          </div>
        </div>

        {displayRegs.length === 0 ? (
          <p className="text-slate-500 text-center py-8">
            {searchQuery ? "No players match your search" : "No registrations in this category"}
          </p>
        ) : (
          <div className="program-table-scroll">
            <table className="w-full">
              <thead>
                <tr className="text-left text-sm text-slate-500 border-b border-slate-200/60">
                  <th className="pb-3 pr-2">
                    <button aria-label="Select all registrations" onClick={toggleSelectAll} className="text-slate-400 hover:text-slate-700 transition-colors">
                      {allSelected ? <CheckSquare className="w-4.5 h-4.5 text-slate-900" /> : <Square className="w-4.5 h-4.5" />}
                    </button>
                  </th>
                  <SortHeader label="Player" field="name" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                  <SortHeader label="Age" field="age" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                  <SortHeader label="DOB" field="dob" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                  <SortHeader label="Gender" field="gender" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                  <SortHeader label={isCamp ? "Group" : "Team"} field="team" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                  <SortHeader label="Status" field="status" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                  <SortHeader label="Payment" field="payment" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                  <SortHeader label="Total" field="total" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                  <SortHeader label="Paid" field="paid" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                  <SortHeader label="Remaining" field="remaining" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                  <SortHeader label="Date" field="date" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                  <th className="pb-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {displayRegs.map((reg) => {
                  const team = teams.find(t => t.team_id === reg.team_id);
                  const playerAge = calculateAge(reg.player?.date_of_birth);
                  const isSelected = selectedIds.has(reg.registration_id);
                  return (
                    <tr
                      key={reg.registration_id}
                      className={`border-b border-gray-100 last:border-0 transition-colors cursor-pointer ${isSelected ? 'bg-slate-50' : 'hover:bg-slate-50/50'}`}
                      onClick={(e) => {
                        // Don't open panel if clicking checkbox, dropdown, or button
                        if (e.target.closest('button') || e.target.closest('[role="menuitem"]')) return;
                        handleOpenDetail(reg);
                      }}
                    >
                      <td className="py-3 pr-2">
                        <button aria-label={`Select ${reg.player?.first_name || "participant"} ${reg.player?.last_name || ""}`} onClick={() => toggleSelect(reg.registration_id)} className="text-slate-400 hover:text-slate-700 transition-colors">
                          {isSelected ? <CheckSquare className="w-4.5 h-4.5 text-slate-900" /> : <Square className="w-4.5 h-4.5" />}
                        </button>
                      </td>
                      <td className="py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full overflow-hidden bg-gray-200 flex items-center justify-center flex-shrink-0">
                            {reg.player?.photo_url ? (
                              <img loading="lazy" src={reg.player.photo_url} alt={reg.player?.first_name} className="w-full h-full object-cover" />
                            ) : (
                              <Users className="w-5 h-5 text-slate-400" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">
                              {reg.player?.first_name || "Unknown"} {reg.player?.last_name || "Player"}
                            </p>
                            {reg.payer?.name && <p className="text-xs text-slate-500">Paid by: {reg.payer.name}</p>}
                            <p className="program-row-meta">{reg.payer?.email || reg.household?.primary_contact?.email || reg.household_email || 'Email unavailable'}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 text-sm text-slate-700">{playerAge !== null ? playerAge : '-'}</td>
                      <td className="py-3 text-sm text-slate-700">{formatDOB(reg.player?.date_of_birth)}</td>
                      <td className="py-3 text-sm text-slate-700 capitalize">{reg.player?.gender || '-'}</td>
                      <td className="py-3">
                        {team ? (
                          <span className="px-2 py-1 text-xs rounded-full" style={{ backgroundColor: team.color ? `${team.color}20` : "#f3f4f6", color: team.color || "#374151" }}>
                            {team.name}
                          </span>
                        ) : <span className="text-slate-400 text-sm">-</span>}
                      </td>
                      <td className="py-3">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          reg.status === "confirmed" ? "bg-green-100 text-green-700" :
                          reg.status === "pending" ? "bg-yellow-100 text-yellow-700" :
                          reg.status === "pre_registered" ? "bg-violet-100 text-violet-700" :
                          reg.status === "tryout" ? "bg-blue-100 text-blue-700" :
                          reg.status === "waitlisted" ? "bg-slate-100 text-slate-700" :
                          reg.status === "declined" ? "bg-red-100 text-red-700" :
                          "bg-slate-100 text-slate-700"
                        }`}>
                          {reg.status === "pre_registered" ? "Pre-Registered" : reg.status === "tryout" ? "Tryout" : reg.status}
                        </span>
                      </td>
                      <td className="py-3">
                        <span className={`px-2 py-1 text-xs rounded-full ${reg.payment_status === "paid" ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-700"}`}>
                          {reg.payment_status || '—'}
                        </span>
                        {reg.payment_plan && <p className="program-row-meta">Plan: {reg.payment_plan}</p>}
                      </td>
                      <td className="py-3 font-medium text-slate-900">${reg.amount}</td>
                      <td className="py-3 text-sm text-green-700 font-medium">
                        ${reg.amount_paid != null ? reg.amount_paid.toFixed(2) : reg.payment_status === 'paid' ? reg.amount : '0.00'}
                      </td>
                      <td className={`py-3 text-sm font-medium ${reg.amount_remaining > 0 ? 'text-amber-700' : 'text-slate-400'}`}>
                        ${reg.amount_remaining != null ? reg.amount_remaining.toFixed(2) : reg.payment_status === 'paid' ? '0.00' : reg.amount}
                      </td>
                      <td className="py-3 text-sm text-slate-500">{new Date(reg.created_at).toLocaleDateString()}</td>
                      <td className="py-3">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button aria-label={`Actions for ${reg.player?.first_name || "participant"} ${reg.player?.last_name || ""}`} variant="ghost" size="sm" className="h-8 w-8 p-0">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleOpenDetail(reg)}>
                              <Eye className="w-4 h-4 mr-2" />View Details
                            </DropdownMenuItem>
                            {registrationSubTab === "pre" && (
                              <>
                                <DropdownMenuItem onClick={async () => {
                                  try {
                                    await axios.post(`${API}/programs/${programId}/send-payment-requests`, { registration_ids: [reg.registration_id] }, { headers });
                                    toast.success("Payment request sent!");
                                  } catch (err) { toast.error("Failed to send"); }
                                }}>
                                  <Mail className="w-4 h-4 mr-2" />Send Payment Request
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={async () => {
                                  try {
                                    await axios.post(`${API}/registrations/${reg.registration_id}/convert-to-pending`, {}, { headers });
                                    toast.success("Converted to pending!");
                                    onRefresh();
                                  } catch (err) { toast.error("Failed to convert"); }
                                }}>
                                  <Check className="w-4 h-4 mr-2" />Open for Payment
                                </DropdownMenuItem>
                              </>
                            )}
                            {registrationSubTab === "tryout" && (
                              <>
                                <DropdownMenuItem onClick={async () => {
                                  try {
                                    await axios.put(`${API}/registrations/${reg.registration_id}`, { status: "confirmed" }, { headers });
                                    toast.success("Player confirmed!");
                                    onRefresh();
                                  } catch (err) { toast.error("Failed to confirm"); }
                                }}>
                                  <Check className="w-4 h-4 mr-2" />Confirm Player
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={async () => {
                                  try {
                                    await axios.put(`${API}/registrations/${reg.registration_id}`, { status: "declined" }, { headers });
                                    toast.success("Player declined");
                                    onRefresh();
                                  } catch (err) { toast.error("Failed to decline"); }
                                }} className="text-red-600">
                                  <X className="w-4 h-4 mr-2" />Decline Player
                                </DropdownMenuItem>
                              </>
                            )}
                            <DropdownMenuSeparator />
                            {reg.payment_status === "paid" && (
                              <DropdownMenuItem onClick={() => handleOpenRefund(reg)} className="text-amber-600">
                                <RefreshCw className="w-4 h-4 mr-2" />Process Refund
                              </DropdownMenuItem>
                            )}
                            {reg.status !== "cancelled" && (
                              <DropdownMenuItem onClick={async () => {
                                if (!window.confirm("Cancel this registration?")) return;
                                try {
                                  await axios.put(`${API}/registrations/${reg.registration_id}/cancel`, {}, { headers });
                                  toast.success("Registration cancelled");
                                  onRefresh();
                                } catch (err) { toast.error("Failed to cancel"); }
                              }} className="text-red-600">
                                <Ban className="w-4 h-4 mr-2" />Cancel Registration
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ==================== FLOATING BULK ACTION BAR ==================== */}
      {selectedCount > 0 && (
        <div className="program-bulk-actions bg-slate-900 text-white">
          <div className="flex-shrink-0">
            <p className="font-semibold text-sm">{selectedCount} selected</p>
          </div>
          <div className="h-6 w-px bg-slate-700" />
          <div className="flex items-center gap-2 flex-wrap">
            <Button size="sm" variant="ghost" className="text-white hover:bg-slate-800 text-xs"
              onClick={() => { setEmailSubject(""); setEmailBody(""); setEmailDialogOpen(true); }}>
              <Mail className="w-3.5 h-3.5 mr-1.5" />Email
            </Button>
            {teams.length > 0 && (
              <Button size="sm" variant="ghost" className="text-white hover:bg-slate-800 text-xs"
                onClick={() => { setSelectedTeamId(""); setTeamDialogOpen(true); }}>
                <UserPlus className="w-3.5 h-3.5 mr-1.5" />Assign Team
              </Button>
            )}
            <Button size="sm" variant="ghost" className="text-white hover:bg-slate-800 text-xs"
              onClick={() => { setBulkStatus(""); setBulkStatusDialogOpen(true); }}>
              <CheckCircle className="w-3.5 h-3.5 mr-1.5" />Change Status
            </Button>
            {selectedRegs.some(r => r.payment_status === "paid") && (
              <Button size="sm" variant="ghost" className="text-amber-400 hover:bg-slate-800 text-xs"
                onClick={() => setBulkRefundDialogOpen(true)}>
                <DollarSign className="w-3.5 h-3.5 mr-1.5" />Refund
              </Button>
            )}
            <Button size="sm" variant="ghost" className="text-white hover:bg-slate-800 text-xs" onClick={exportRegistrations}>
              <Download className="w-3.5 h-3.5 mr-1.5" />Export
            </Button>
          </div>
          <div className="h-6 w-px bg-slate-700" />
          <Button size="sm" variant="ghost" className="text-slate-400 hover:text-white" onClick={() => setSelectedIds(new Set())}>
            <XCircle className="w-4 h-4" />
          </Button>
        </div>
      )}

      {/* ==================== BULK EMAIL DIALOG ==================== */}
      <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Email {selectedCount} Player{selectedCount > 1 ? 's' : ''}</DialogTitle>
            <DialogDescription>
              Send an email to the parents/guardians of selected players.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <label htmlFor="program-bulk-email-subject" className="text-sm font-medium text-slate-700">Subject</label>
              <input
                id="program-bulk-email-subject"
                type="text" value={emailSubject} onChange={e => setEmailSubject(e.target.value)}
                placeholder="e.g. Practice schedule update"
                className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-slate-300"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Message</label>
              <textarea
                value={emailBody} onChange={e => setEmailBody(e.target.value)}
                placeholder="Type your message..."
                rows={6}
                className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-slate-300 resize-none"
              />
            </div>
            <div className="bg-slate-50 rounded-xl p-3 text-xs text-slate-500">
              <p className="font-medium text-slate-700 mb-1">Recipients:</p>
              <p>{[...new Set(selectedRegs.map(r => r.payer?.email || r.household?.primary_contact?.email || r.household_email).filter(Boolean))].join(', ') || 'No emails found'}</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailDialogOpen(false)} className="rounded-xl">Cancel</Button>
            <Button onClick={handleBulkEmail} disabled={sendingEmail} className="bg-slate-900 text-white hover:bg-slate-800 rounded-xl">
              {sendingEmail ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending...</> : <><Mail className="w-4 h-4 mr-2" />Send Email</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ==================== ASSIGN TEAM DIALOG ==================== */}
      <Dialog open={teamDialogOpen} onOpenChange={setTeamDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Assign {selectedCount} Player{selectedCount > 1 ? 's' : ''} to Team</DialogTitle>
            <DialogDescription>Select a team to assign the selected players to.</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Select value={selectedTeamId} onValueChange={setSelectedTeamId}>
              <SelectTrigger className="rounded-xl">
                <SelectValue placeholder="Select a team..." />
              </SelectTrigger>
              <SelectContent>
                {teams.map(t => (
                  <SelectItem key={t.team_id} value={t.team_id}>
                    <div className="flex items-center gap-2">
                      {t.color && <div className="w-3 h-3 rounded-full" style={{ backgroundColor: t.color }} />}
                      {t.name}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTeamDialogOpen(false)} className="rounded-xl">Cancel</Button>
            <Button onClick={handleBulkAssignTeam} disabled={assigningTeam || !selectedTeamId} className="bg-slate-900 text-white hover:bg-slate-800 rounded-xl">
              {assigningTeam ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Assigning...</> : <><UserPlus className="w-4 h-4 mr-2" />Assign Team</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ==================== BULK STATUS DIALOG ==================== */}
      <Dialog open={bulkStatusDialogOpen} onOpenChange={setBulkStatusDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Change Status for {selectedCount} Registration{selectedCount > 1 ? 's' : ''}</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Select value={bulkStatus} onValueChange={setBulkStatus}>
              <SelectTrigger className="rounded-xl">
                <SelectValue placeholder="Select new status..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="confirmed">Confirmed</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="waitlisted">Waitlisted</SelectItem>
                <SelectItem value="declined">Declined</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkStatusDialogOpen(false)} className="rounded-xl">Cancel</Button>
            <Button onClick={handleBulkStatusChange} disabled={bulkUpdatingStatus || !bulkStatus} className="bg-slate-900 text-white hover:bg-slate-800 rounded-xl">
              {bulkUpdatingStatus ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Updating...</> : <><Check className="w-4 h-4 mr-2" />Update Status</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ==================== BULK REFUND DIALOG ==================== */}
      <Dialog open={bulkRefundDialogOpen} onOpenChange={setBulkRefundDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Refund {selectedRegs.filter(r => r.payment_status === "paid").length} Registration{selectedRegs.filter(r => r.payment_status === "paid").length > 1 ? 's' : ''}</DialogTitle>
            <DialogDescription>
              This will process a full refund for all selected paid registrations. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-2">
            {selectedRegs.filter(r => r.payment_status === "paid").map(r => (
              <div key={r.registration_id} className="flex justify-between text-sm bg-slate-50 rounded-xl px-4 py-2">
                <span>{r.player?.first_name} {r.player?.last_name}</span>
                <span className="font-medium">${r.amount_paid?.toFixed(2) || r.amount}</span>
              </div>
            ))}
            <div className="flex justify-between font-semibold text-sm pt-2 border-t border-slate-200">
              <span>Total Refund</span>
              <span>${selectedRegs.filter(r => r.payment_status === "paid").reduce((s, r) => s + (r.amount_paid || r.amount || 0), 0).toFixed(2)}</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkRefundDialogOpen(false)} className="rounded-xl">Cancel</Button>
            <Button onClick={handleBulkRefund} disabled={bulkRefunding} className="bg-red-600 text-white hover:bg-red-700 rounded-xl">
              {bulkRefunding ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Processing...</> : <><RefreshCw className="w-4 h-4 mr-2" />Process Refunds</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Registration Detail Panel (slide-out) */}
      <RegistrationDetailPanel
        open={detailPanelOpen}
        onClose={() => { setDetailPanelOpen(false); setDetailRegistration(null); }}
        registration={detailRegistration}
        program={program}
        teams={teams}
        token={token}
        onRefresh={onRefresh}
        onOpenRefund={(reg) => {
          setDetailPanelOpen(false);
          handleOpenRefund(reg);
        }}
      />

      {/* Refund Dialog */}
      <RefundDialog
        open={refundDialogOpen}
        onOpenChange={setRefundDialogOpen}
        registration={refundRegistration}
        onRefundComplete={handleRefundComplete}
      />
    </div>
  );
};

export default ProgramRegistrationsTab;
